import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS, Toggle} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import ContentBlockBar from './ContentBlockBar';
import {NarrativeEditor} from './common';
import {defaultSectionTitleId, sectionTitle} from '../config/defaultTemplate';
import {sectionOf, withSection} from '../persistence/quoteTemplateSchema';
import {applyContentBlock, sourceBlockState} from '../persistence/sourceBlockState';
import {showEditSectionTitleModal} from '../modals/EditSectionTitleModal';
import {showSaveContentBlockModal} from '../modals/SaveContentBlockModal';
import type {QuoteContentBlock} from '../persistence/sourceBlockState';
import type {MergeField} from '../config/mergeFields';
import type {QuoteSectionType, QuoteTemplate} from '../resolver/types';

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
`;

const TitleGroup = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	flex: 1;
`;

/* The versions bar and the editable read as one control, so the box clips them together. */
const EditorBox = styled.div`
	display: flex;
	flex-direction: column;
	border-radius: 4px;
	overflow: hidden;
`;

const Title = styled.span`
	font-size: 13px;
	font-weight: 600;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

type Props = {
	type: QuoteSectionType;
	template: QuoteTemplate;
	onChange: (next: QuoteTemplate) => void;
	mergeFields: MergeField[];
	blocks: readonly QuoteContentBlock[];
	placeholderId: string;
	onSaveBlock: (block: QuoteContentBlock, body: string) => void;
	onSaveBlockAs: (type: QuoteSectionType, name: string, body: string) => void;
	onSetBlockDefault: (block: QuoteContentBlock) => void;
};

/** One authored section: its heading, its include toggle, its copy, and its saved-content controls. */
const AuthoredSectionBlock: React.FC<Props> = ({
	type,
	template,
	onChange,
	mergeFields,
	blocks,
	placeholderId,
	onSaveBlock,
	onSaveBlockAs,
	onSetBlockDefault,
}) => {
	const intl = useIntl();
	// The design-system editor takes an `initialValue`: it seeds its document once and never reads
	// the prop again, so applying a saved version changed the quote and the rendered preview but
	// left the editable showing the copy it was mounted with. Remounting is what puts the applied
	// copy in front of the author. Counting applies rather than keying on the block's id, so
	// re-picking the version already in use still restores it after an edit.
	const [applyCount, setApplyCount] = useState(0);
	const section = sectionOf(template, type);
	if (!section) return null;

	const defaultTitle = intl.formatMessage({id: defaultSectionTitleId(type)});
	// The same resolution the document uses, so the panel label and the rendered heading agree.
	const heading = sectionTitle(template, type, intl);
	const serializedBody = JSON.stringify(section.body);

	const handleRename = () =>
		showEditSectionTitleModal({
			currentTitle: section.title ?? '',
			defaultTitle,
			// A cleared name is not an empty heading — it means "use the localized default again",
			// which is exactly what a null title stores.
			onSave: (next: string) => onChange(withSection(template, type, {title: next.trim() || null})),
		});

	return (
		<>
			<Header>
				<TitleGroup>
					<Title>{heading}</Title>
					{/* The section is in the name because the three pencils are otherwise identical. */}
					<Button
						emphasis={EMPHASIS.LOW}
						icon="edit"
						iconOnly
						onPress={handleRename}
						aria-label={intl.formatMessage({id: 'quote_designer.section_title.edit_section'}, {section: heading})}
						data-cy={`quote-section-rename-${type}`}
					/>
				</TitleGroup>
				<Toggle
					checked={section.enabled}
					onChange={(checked: boolean) => onChange(withSection(template, type, {enabled: checked}))}
					aria-label={heading}
					cy={`quote-toggle-${type.toLowerCase()}`}
				/>
			</Header>

			{section.enabled && (
				<>
					<EditorBox>
						<ContentBlockBar
							sectionType={type}
							sectionLabel={heading}
							blocks={blocks}
							state={sourceBlockState(section, blocks)}
							selectedBlockId={section.sourceBlockId}
							onApply={block => {
								setApplyCount(count => count + 1);
								onChange(applyContentBlock(template, block));
							}}
							onSave={block => onSaveBlock(block, serializedBody)}
							onSaveAs={() =>
								showSaveContentBlockModal({
									sectionLabel: heading,
									onSave: name => onSaveBlockAs(type, name, serializedBody),
								})
							}
							onSetDefault={onSetBlockDefault}
						/>
						<NarrativeEditor
							key={applyCount}
							cy={`quote-narrative-${type.toLowerCase()}`}
							value={section.body}
							disabled={false}
							placeholder={intl.formatMessage({id: placeholderId})}
							mergeFields={mergeFields}
							onChange={value => onChange(withSection(template, type, {body: value}))}
						/>
					</EditorBox>
				</>
			)}
		</>
	);
};

export default AuthoredSectionBlock;
